// ============================================================================
// ÄNDERUNGSPROTOKOLL - StatsSection.js
// ============================================================================
//
// DATUM: 25.11.2025
//
// ZWECK DER ÄNDERUNG:
// Die StatsSection (Statistik-Anzeige mit "85 Städte", "350.000 Fahrten pro Jahr",
// "4,5 Millionen Euro gespart") wurde temporär ausgeblendet, da diese Zahlen
// aktuell nicht im Frontend angezeigt werden sollen.
//
// AUSGANGSPUNKT (VORHER):
// - Die Komponente war vollständig aktiv und renderte drei Statistik-Karten
// - Imports: useState, useEffect, useRef aus 'react' und useTranslation
// - Die Komponente enthielt eine animierte Zähler-Funktion für die Städte-Zahl
// - Return: Ein vollständiges JSX mit drei Stat-Karten (Icons, Zahlen, Labels)
//
// ENDPUNKT (NACHHER):
// - Komponente gibt jetzt `return null` zurück → nichts wird gerendert
// - Imports wurden auskommentiert (nicht mehr benötigt)
// - Gesamte Original-Implementierung ist in einem Block-Kommentar erhalten
// - Code kann jederzeit reaktiviert werden durch:
//   1. Imports wieder einkommentieren
//   2. `return null;` entfernen
//   3. Block-Kommentar um die Original-Implementierung entfernen
//
// VERKNÜPFUNG:
// Diese Änderung hängt zusammen mit PlatformSection.js - da StatsSection
// ausgeblendet wurde, wurde dort das margin-top reduziert, um den Leerraum
// zu kompensieren.
// ============================================================================

// TEMPORARILY COMMENTED OUT - Stats section hidden from frontend
// import { useState, useEffect, useRef } from 'react';
// import { useTranslation } from '../i18n/useTranslation';

const StatsSection = () => {
  // Temporarily returning null to hide stats from frontend
  return null;

  /* ORIGINAL IMPLEMENTATION - COMMENTED OUT
  const { t } = useTranslation();
  const [cityCount, setCityCount] = useState(1);
  const [hasAnimated, setHasAnimated] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);

            const duration = 3000; // 3 seconds
            const targetValue = 85;
            const startTime = Date.now();

            const animateCounter = () => {
              const currentTime = Date.now();
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);

              // Easing function for smooth animation
              const easeOutQuart = 1 - Math.pow(1 - progress, 4);
              const currentValue = Math.floor(1 + (targetValue - 1) * easeOutQuart);

              setCityCount(currentValue);

              if (progress < 1) {
                requestAnimationFrame(animateCounter);
              }
            };

            animateCounter();
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% visible
        rootMargin: '0px 0px -100px 0px' // Add negative bottom margin to trigger later
      }
    );

    const currentRef = statsRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasAnimated]);

  const stats = [
    {
      number: cityCount.toString(),
      label: t('stats.cities'),
      icon: (
        <svg className="w-20 h-20" style={{ color: '#93c5fd' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m-1 4h1m5-4h1m-1 4h1m-1-4h1m-1 4h1"></path>
        </svg>
      )
    },
    {
      number: t('stats.tripsNumber'),
      label: t('stats.trips'),
      icon: (
        <svg className="w-20 h-20" style={{ color: '#fdba74' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
        </svg>
      )
    },
    {
      number: t('stats.savingsNumber'),
      label: t('stats.savings'),
      icon: (
        <svg className="w-20 h-20" style={{ color: '#93c5fd' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      )
    }
  ];

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative mt-[-250px] min-[1800px]:mt-[220px]" style={{ zIndex: 20 }}>
      <div ref={statsRef} className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
        {stats.map((stat, index) => (
          <div key={index} className="flex flex-col items-center relative" style={{ zIndex: 20 }}>
            <div className="w-36 h-36 min-[1800px]:w-48 min-[1800px]:h-48 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center mb-6"
              style={{ boxShadow: 'rgba(14, 103, 231, 0.5) 0px 8px 30px 0px' }}>
              {stat.icon}
            </div>
            <p className="text-4xl lg:text-5xl min-[1800px]:text-7xl font-extrabold text-brand-dark-blue drop-shadow-lg">{stat.number}</p>
            <p className="text-lg min-[1800px]:text-2xl text-gray-700 font-semibold mt-2 drop-shadow-lg">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
  */
};

export default StatsSection;